import axios, { AxiosInstance } from 'axios';
import { 
  Customer, 
  StitchItem, 
  Measurement, 
  Order, 
  OrderItem, 
  Payment,
  PaymentFormData,
  DashboardStats,
  ShopSettings,
  ApiResponse,
  CustomerFormData,
  OrderApiData,
  User,
  LoginCredentials,
  LoginResponse,
  PaginationParams,
  OrderPaginationParams,
  Subscription,
  SubscriptionFormData,
  SubscriptionNotification,
  SubscriptionStats,
  Wallet,
  WalletTransaction,
  WalletBalance,
  WalletTopupData,
  WalletTransactionFilters
} from '../types';
import { API_BASE_URL, API_ENDPOINTS } from '../utils/constants';

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Add request interceptor to include auth token and show loading
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Show loading for non-GET requests or specific endpoints
    const shouldShowLoading = config.method !== 'get' || 
      config.url?.includes('/auth.php') || 
      config.url?.includes('/dashboard.php');
    
    if (shouldShowLoading && typeof window !== 'undefined') {
      // Dispatch custom event for loading
      window.dispatchEvent(new CustomEvent('api-loading-start', { 
        detail: { message: 'Processing request...' } 
      }));
    }
    
    return config;
  },
  (error) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('api-loading-end'));
    }
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Hide loading on successful response
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('api-loading-end'));
    }
    return response;
  },
  (error) => {
    console.error('API Error:', error);
    
    // Hide loading on error
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('api-loading-end'));
    }
    
    // Handle authentication errors
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    
    // Handle account deactivation
    if (error.response?.data?.message === 'Account has been deactivated') {
      localStorage.removeItem('auth_token');
      alert('Your account has been deactivated. Please contact the administrator.');
      window.location.href = '/login';
    }
    
    // Handle user not found or inactive
    if (error.response?.data?.message === 'User not found or inactive') {
      localStorage.removeItem('auth_token');
      alert('Your account is no longer active. Please contact the administrator.');
      window.location.href = '/login';
    }
    
    // Handle account blocked
    if (error.response?.data?.message === 'Account blocked by Admin. Please contact Admin') {
      // Don't redirect, let the AuthContext handle this error
    }
    
    return Promise.reject(error);
  }
);

// Authentication API
export const authApi = {
  login: (credentials: LoginCredentials): Promise<ApiResponse<LoginResponse>> => 
    api.post('/auth.php', credentials).then(res => res.data).catch(error => {
      // Re-throw the error so it can be caught by the AuthContext
      throw error;
    }),
  
  verifyToken: (): Promise<ApiResponse<{ user: User }>> => {
    const token = localStorage.getItem('auth_token');
    return api.get('/auth.php', {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }).then(res => res.data);
  },
  
  logout: (): Promise<ApiResponse<null>> => 
    api.post('/auth.php?action=logout').then(res => res.data),
  
  changePassword: (passwords: { current_password: string; new_password: string }): Promise<ApiResponse<null>> => 
    api.post('/auth.php?action=change-password', passwords).then(res => res.data),
  
  requestPasswordReset: (data: { email: string }): Promise<ApiResponse<{ message: string }>> => 
    api.post('/auth.php?action=forgot-password', data).then(res => res.data),
  
  verifyResetCode: (data: { email: string; reset_code: string }): Promise<ApiResponse<{ message: string }>> => 
    api.post('/auth.php?action=verify-reset-code', data).then(res => res.data),
  
  resetPassword: (data: { email: string; reset_code: string; new_password: string }): Promise<ApiResponse<{ message: string }>> => 
    api.post('/auth.php?action=reset-password', data).then(res => res.data),
};

// Customer API
export const customerApi = {
  getAll: (params?: PaginationParams): Promise<ApiResponse<Customer[]>> => {
    const queryParams = new URLSearchParams();
    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);
    }
    const url = queryParams.toString() ? `${API_ENDPOINTS.CUSTOMERS}?${queryParams}` : API_ENDPOINTS.CUSTOMERS;
    return api.get(url).then(res => res.data);
  },
  
  getAllForOrders: (params?: PaginationParams): Promise<ApiResponse<Customer[]>> => {
    const queryParams = new URLSearchParams();
    queryParams.append('include_deleted', 'true');
    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);
    }
    const url = `${API_ENDPOINTS.CUSTOMERS}?${queryParams}`;
    return api.get(url).then(res => res.data);
  },
  
  getById: (id: number): Promise<ApiResponse<Customer>> => 
    api.get(`${API_ENDPOINTS.CUSTOMERS}?id=${id}`).then(res => res.data),
  
  search: (query: string): Promise<ApiResponse<Customer[]>> => 
    api.get(`${API_ENDPOINTS.CUSTOMERS}?search=${encodeURIComponent(query)}`).then(res => res.data),
  
  create: (customer: CustomerFormData): Promise<ApiResponse<Customer>> => 
    api.post(API_ENDPOINTS.CUSTOMERS, customer).then(res => res.data),
  
  update: (id: number, customer: Partial<CustomerFormData>): Promise<ApiResponse<Customer>> => 
    api.put(`${API_ENDPOINTS.CUSTOMERS}?id=${id}`, customer).then(res => res.data),
  
  delete: (id: number): Promise<ApiResponse<null>> => 
    api.delete(API_ENDPOINTS.CUSTOMERS, { data: { id } }).then(res => res.data),
  
  getDeleted: (params?: PaginationParams): Promise<ApiResponse<Customer[]>> => {
    const queryParams = new URLSearchParams();
    queryParams.append('action', 'deleted');
    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);
    }
    const url = `${API_ENDPOINTS.CUSTOMERS}?${queryParams}`;
    return api.get(url).then(res => res.data);
  },
  
  restore: (id: number): Promise<ApiResponse<null>> => 
    api.post(`${API_ENDPOINTS.CUSTOMERS}?action=restore`, { id }).then(res => res.data),
};

// Stitch Items API
export const stitchItemApi = {
  getAll: (): Promise<ApiResponse<StitchItem[]>> => 
    api.get(API_ENDPOINTS.STITCH_ITEMS).then(res => res.data),
  
  getById: (id: number): Promise<ApiResponse<StitchItem>> => 
    api.get(`${API_ENDPOINTS.STITCH_ITEMS}?id=${id}`).then(res => res.data),
  
  create: (item: Omit<StitchItem, 'id' | 'created_at'>): Promise<ApiResponse<StitchItem>> => 
    api.post(API_ENDPOINTS.STITCH_ITEMS, item).then(res => res.data),
  
  update: (id: number, item: Partial<StitchItem>): Promise<ApiResponse<StitchItem>> => 
    api.put(`${API_ENDPOINTS.STITCH_ITEMS}?id=${id}`, item).then(res => res.data),
  
  delete: (id: number): Promise<ApiResponse<null>> => 
    api.delete(`${API_ENDPOINTS.STITCH_ITEMS}?id=${id}`).then(res => res.data),
};

// Measurements API
export const measurementApi = {
  getCustomerItemMeasurements: (customerId: number, stitchItemId: number): Promise<ApiResponse<Measurement[]>> => 
    api.get(`${API_ENDPOINTS.MEASUREMENTS}?customer_id=${customerId}&stitch_item_id=${stitchItemId}`).then(res => res.data),
  
  getCustomerMeasurements: (customerId: number): Promise<ApiResponse<Measurement[]>> => 
    api.get(`${API_ENDPOINTS.MEASUREMENTS}?customer_id=${customerId}`).then(res => res.data),
  
  getStandardMeasurements: (stitchItemId: number): Promise<ApiResponse<string[]>> => 
    api.get(`${API_ENDPOINTS.MEASUREMENTS}?stitch_item_id=${stitchItemId}`).then(res => res.data),
  
  saveMeasurements: (customerId: number, stitchItemId: number, measurements: Measurement[]): Promise<ApiResponse<null>> => 
    api.post(API_ENDPOINTS.MEASUREMENTS, {
      customer_id: customerId,
      stitch_item_id: stitchItemId,
      measurements: measurements
    }).then(res => res.data),
  
  deleteMeasurements: (customerId: number, stitchItemId: number): Promise<ApiResponse<null>> => 
    api.delete(`${API_ENDPOINTS.MEASUREMENTS}?customer_id=${customerId}&stitch_item_id=${stitchItemId}`).then(res => res.data),
};

// Orders API
export const orderApi = {
  getAll: (params?: OrderPaginationParams): Promise<ApiResponse<Order[]>> => {
    const queryParams = new URLSearchParams();
    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.status) queryParams.append('status', params.status);
      if (params.date_from) queryParams.append('date_from', params.date_from);
      if (params.date_to) queryParams.append('date_to', params.date_to);
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);
    }
    const url = queryParams.toString() ? `${API_ENDPOINTS.ORDERS}?${queryParams}` : API_ENDPOINTS.ORDERS;
    return api.get(url).then(res => res.data);
  },
  
  getById: (id: number): Promise<ApiResponse<Order>> => 
    api.get(`${API_ENDPOINTS.ORDERS}?id=${id}`).then(res => res.data),
  
  getByCustomer: (customerId: number, params?: OrderPaginationParams): Promise<ApiResponse<Order[]>> => {
    const queryParams = new URLSearchParams();
    queryParams.append('customer_id', customerId.toString());
    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.status) queryParams.append('status', params.status);
      if (params.date_from) queryParams.append('date_from', params.date_from);
      if (params.date_to) queryParams.append('date_to', params.date_to);
      if (params.sort_by) queryParams.append('sort_by', params.sort_by);
      if (params.sort_order) queryParams.append('sort_order', params.sort_order);
    }
    const url = `${API_ENDPOINTS.ORDERS}?${queryParams}`;
    return api.get(url).then(res => res.data);
  },
  
  getByStatus: (status: string): Promise<ApiResponse<Order[]>> => 
    api.get(`${API_ENDPOINTS.ORDERS}?status=${status}`).then(res => res.data),
  
  create: (order: OrderApiData): Promise<ApiResponse<Order>> => 
    api.post(API_ENDPOINTS.ORDERS, order).then(res => res.data),
  
  update: (id: number, order: Partial<OrderApiData>): Promise<ApiResponse<Order>> => 
    api.put(`${API_ENDPOINTS.ORDERS}?id=${id}`, order).then(res => res.data),
  
  delete: (id: number): Promise<ApiResponse<null>> => 
    api.delete(`${API_ENDPOINTS.ORDERS}?id=${id}`).then(res => res.data),
  
  updateStatus: (id: number, status: string): Promise<ApiResponse<Order>> => 
    api.put(`${API_ENDPOINTS.ORDERS}?id=${id}`, { status }).then(res => res.data),
  
  getPreviousMeasurements: (customerId: number, stitchItemId: number): Promise<ApiResponse<{measurements: any[], order_date: string}>> => 
    api.get(`${API_ENDPOINTS.ORDERS}?action=get_previous_measurements&customer_id=${customerId}&stitch_item_id=${stitchItemId}`).then(res => res.data),
};

// Payments API
export const paymentApi = {
  getByOrder: (orderId: number): Promise<ApiResponse<Payment[]>> => 
    api.get(`${API_ENDPOINTS.PAYMENTS}?order_id=${orderId}`).then(res => res.data),
  
  create: (payment: PaymentFormData): Promise<ApiResponse<Payment>> => 
    api.post(API_ENDPOINTS.PAYMENTS, payment).then(res => res.data),
  
  update: (id: number, payment: Partial<PaymentFormData>): Promise<ApiResponse<Payment>> => 
    api.put(`${API_ENDPOINTS.PAYMENTS}?id=${id}`, payment).then(res => res.data),
  
  delete: (id: number): Promise<ApiResponse<null>> => 
    api.delete(`${API_ENDPOINTS.PAYMENTS}?id=${id}`).then(res => res.data),
};

// Dashboard API
export const dashboardApi = {
  getStats: (): Promise<ApiResponse<DashboardStats>> => 
    api.get(`${API_ENDPOINTS.DASHBOARD}?type=stats`).then(res => res.data),
  
  getTodayOrders: (): Promise<ApiResponse<Order[]>> => 
    api.get(`${API_ENDPOINTS.DASHBOARD}?type=today_orders`).then(res => res.data),
  
  getDeliveryOrders: (): Promise<ApiResponse<Order[]>> => 
    api.get(`${API_ENDPOINTS.DASHBOARD}?type=delivery_orders`).then(res => res.data),
  
  getRevenueAnalytics: (params?: { period?: string; custom_from?: string; custom_to?: string }): Promise<ApiResponse<any>> => {
    const queryParams = new URLSearchParams();
    if (params) {
      if (params.period) queryParams.append('period', params.period);
      if (params.custom_from) queryParams.append('custom_from', params.custom_from);
      if (params.custom_to) queryParams.append('custom_to', params.custom_to);
    }
    const url = queryParams.toString() ? `${API_ENDPOINTS.DASHBOARD}?type=revenue_analytics&${queryParams}` : `${API_ENDPOINTS.DASHBOARD}?type=revenue_analytics`;
    return api.get(url).then(res => res.data);
  },
  
  getPendingPayments: (): Promise<ApiResponse<any>> => 
    api.get(`${API_ENDPOINTS.DASHBOARD}?type=pending_payments`).then(res => res.data),
  
  getChartData: (period?: string): Promise<ApiResponse<any>> => {
    const queryParams = new URLSearchParams();
    if (period) queryParams.append('period', period);
    const url = queryParams.toString() ? `${API_ENDPOINTS.DASHBOARD}?type=chart_data&${queryParams}` : `${API_ENDPOINTS.DASHBOARD}?type=chart_data`;
    return api.get(url).then(res => res.data);
  },
};

// Shop Settings API
export const shopSettingsApi = {
  getAll: (): Promise<ApiResponse<ShopSettings>> => 
    api.get(API_ENDPOINTS.SHOP_SETTINGS).then(res => res.data),
  
  update: (settings: Partial<ShopSettings>): Promise<ApiResponse<null>> => 
    api.put(API_ENDPOINTS.SHOP_SETTINGS, settings).then(res => res.data),
  
  reset: (): Promise<ApiResponse<null>> => 
    api.post(API_ENDPOINTS.SHOP_SETTINGS).then(res => res.data),
};

// Subscription API
export const subscriptionApi = {
  getUserSubscription: (): Promise<ApiResponse<Subscription>> => 
    api.get(`${API_ENDPOINTS.SUBSCRIPTIONS}?action=user_subscription`).then(res => res.data),
  
  getNotifications: (): Promise<ApiResponse<SubscriptionNotification[]>> => 
    api.get(`${API_ENDPOINTS.SUBSCRIPTIONS}?action=notifications`).then(res => res.data),
  
  markNotificationRead: (notificationId: number): Promise<ApiResponse<null>> => 
    api.post(`${API_ENDPOINTS.SUBSCRIPTIONS}?action=mark_notification_read`, { notification_id: notificationId }).then(res => res.data),
  
  // Admin functions
  getAll: (params?: { page?: number; limit?: number; search?: string; status?: string }): Promise<ApiResponse<{ subscriptions: Subscription[]; pagination: any }>> => {
    const queryParams = new URLSearchParams();
    queryParams.append('action', 'admin_all');
    if (params) {
      if (params.page) queryParams.append('page', params.page.toString());
      if (params.limit) queryParams.append('limit', params.limit.toString());
      if (params.search) queryParams.append('search', params.search);
      if (params.status) queryParams.append('status', params.status);
    }
    const url = `${API_ENDPOINTS.SUBSCRIPTIONS}?${queryParams}`;
    return api.get(url).then(res => res.data);
  },
  
  getStats: (): Promise<ApiResponse<SubscriptionStats>> => 
    api.get(`${API_ENDPOINTS.SUBSCRIPTIONS}?action=stats`).then(res => res.data),
  
  update: (subscription: SubscriptionFormData): Promise<ApiResponse<null>> => 
    api.post(`${API_ENDPOINTS.SUBSCRIPTIONS}?action=update`, subscription).then(res => res.data),
};

// Wallet API
export const walletApi = {
  getBalance: (): Promise<ApiResponse<WalletBalance>> => 
    api.get('/wallet.php/balance').then(res => res.data),
  
  getWallet: (): Promise<ApiResponse<Wallet>> => 
    api.get('/wallet.php').then(res => res.data),
  
  getTransactions: (filters?: WalletTransactionFilters): Promise<ApiResponse<WalletTransaction[]>> => {
    const queryParams = new URLSearchParams();
    if (filters) {
      if (filters.page) queryParams.append('page', filters.page.toString());
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.transaction_type) queryParams.append('transaction_type', filters.transaction_type);
      if (filters.date_from) queryParams.append('date_from', filters.date_from);
      if (filters.date_to) queryParams.append('date_to', filters.date_to);
    }
    const url = queryParams.toString() ? `/wallet.php/transactions?${queryParams}` : '/wallet.php/transactions';
    return api.get(url).then(res => res.data);
  },
  
  topup: (topupData: WalletTopupData): Promise<ApiResponse<{ new_balance: number; amount_added: number }>> => 
    api.post('/wallet.php/topup', topupData).then(res => res.data),
  
  createWallet: (): Promise<ApiResponse<null>> => 
    api.post('/wallet.php').then(res => res.data),
};

// Utility functions
export const calculateOrderTotal = (items: OrderItem[]): number => {
  return items.reduce((total, item) => {
    return total + (parseFloat(item.total_price) || 0);
  }, 0);
};

export const calculateBalance = (total: number, advance: number): number => {
  return Math.max(0, total - advance);
};

export default api; 